/**
 * Moment.js → Day.js compatibility layer.
 *
 * The whole application used to import `moment` / `moment-timezone` directly in ~80 files. This
 * module exposes a drop-in `moment` function backed by Day.js (same chaining, same format tokens,
 * same locales) so every existing call-site keeps working while Moment is no longer bundled.
 *
 * Loaded plugins (subset used across the app):
 *   utc, advancedFormat, localeData, localizedFormat, customParseFormat, relativeTime,
 *   duration, isSameOrBefore, isSameOrAfter, isBetween, quarterOfYear, weekOfYear, isoWeek, weekday.
 *
 * NOTE: the dayjs `timezone` plugin is deliberately NOT loaded. react-big-calendar's dayjs
 * localizer switches to a timezone-aware path that performs an Intl.DateTimeFormat offset
 * computation for every date operation once the shared dayjs instance has `.tz`, making
 * month views with hundreds/thousands of events take many seconds (app freeze).
 *
 * Usage:
 *   import moment from '.../helpers/momentCompat';   // behaves like the old `moment`
 */
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import localeData from 'dayjs/plugin/localeData';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import duration from 'dayjs/plugin/duration';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isBetween from 'dayjs/plugin/isBetween';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import isoWeek from 'dayjs/plugin/isoWeek';
import weekday from 'dayjs/plugin/weekday';
import 'dayjs/locale/fr';

dayjs.extend(utc);
dayjs.extend(advancedFormat);
dayjs.extend(localeData);
dayjs.extend(localizedFormat);
dayjs.extend(customParseFormat);
dayjs.extend(relativeTime);
dayjs.extend(duration);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(isBetween);
dayjs.extend(quarterOfYear);
dayjs.extend(weekOfYear);
dayjs.extend(isoWeek);
dayjs.extend(weekday);

// Moment exposes singular + plural accessors (`hours()` vs dayjs `hour()`). Add the plural aliases
// so existing call-sites like `moment(...).hours()` keep working.
const dayjsProto: any = (dayjs as any).prototype;
const pluralUnit = (singularGetter: string) => function pluralAlias(this: any, value?: any) {
    if (arguments.length > 0) {
        return this[singularGetter](value);
    }
    return this[singularGetter]();
};
if (dayjsProto && typeof dayjsProto.hour === 'function') {
    if (!dayjsProto.hours) dayjsProto.hours = pluralUnit('hour');
    if (!dayjsProto.minutes) dayjsProto.minutes = pluralUnit('minute');
    if (!dayjsProto.seconds) dayjsProto.seconds = pluralUnit('second');
    if (!dayjsProto.milliseconds) dayjsProto.milliseconds = pluralUnit('millisecond');
    if (!dayjsProto.unix) {
        dayjsProto.unix = function momentUnixAlias(this: any) {
            return Math.floor(this.valueOf() / 1000);
        };
    }
}

// Moment unit names used with `.get()` / `.set()` are often plural. Day.js only understands the
// singular forms, and no call-site in this codebase passes plural units — so we do NOT wrap
// `dayjs.prototype.get/set` here: replacing those methods corrupts day.js' own date arithmetic
// (every `.add()/.set()/.date()` then silently picks up the current wall-clock time), which in
// turn made react-big-calendar's month-range loop run ~forever (the whole-app freeze).

/** Moment-style `duration('15:30')` string parsing → milliseconds. */
const parseDurationString = (value: string): number | null => {
    const parts = value.split(':').map((p) => Number(p));
    if (parts.length < 2 || parts.some((n) => Number.isNaN(n))) {
        return null;
    }
    const negative = /^-/.test(value.trim());
    const hours = Math.abs(parts[0] || 0);
    const minutes = Math.abs(parts[1] || 0);
    const seconds = parts.length > 2 ? Math.abs(parts[2] || 0) : 0;
    const totalMs = (hours * 3600 + minutes * 60 + seconds) * 1000;
    return negative ? -totalMs : totalMs;
};

/** Detects an explicit zone/offset inside an ISO-ish string (the old moment kept those offsets). */
const hasExplicitOffset = (value: string): boolean => /(?:[+-]\d{2}:?\d{2}|Z)$/i.test(value.trim());

const momentCompat: any = function momentCompat(input?: any, format?: any, strict?: any) {
    // The old app ran with moment.tz.setDefault('UTC') — keep Day.js in UTC by default so the
    // displayed wall-clock values stay byte-for-byte identical to before.
    if (input == null || input === '') {
        return (dayjs as any).utc();
    }
    if (input instanceof Date) {
        return (dayjs as any).utc(input);
    }
    if (dayjs.isDayjs(input)) {
        return (dayjs as any).utc(input.valueOf());
    }
    if (typeof input === 'number') {
        return (dayjs as any).utc(input);
    }
    if (Array.isArray(input)) {
        // moment accepted [year, month(0-based), day, hour, minute, second, millisecond]
        const [y = 0, m = 0, d = 1, h = 0, min = 0, s = 0, ms = 0] = input;
        return (dayjs as any).utc(new Date(y, m, d, h, min, s, ms));
    }
    if (typeof input === 'string') {
        if (format != null) {
            return (dayjs as any).utc(input, format, strict);
        }
        return hasExplicitOffset(input) ? dayjs(input) : (dayjs as any).utc(input);
    }
    if (typeof input === 'object' && input !== null) {
        const {year = 0, month = 0, date = 1, hours = 0, minutes = 0, seconds = 0, milliseconds = 0} = input as any;
        if (Object.keys(input).length) {
            return (dayjs as any).utc(new Date(year, month, date, hours, minutes, seconds, milliseconds));
        }
    }
    return (dayjs as any).utc(input);
};

momentCompat.dayjs = dayjs;
momentCompat.isDayjs = dayjs.isDayjs.bind(dayjs);
momentCompat.version = 'dayjs-compat';

/**
 * moment.locale() / moment.locale('fr', {…config…})
 * Day.js loads its own French locale (already registered above); a passed config is applied via
 * `dayjs.updateLocale` when possible and otherwise ignored (moment-only locale keys).
 */
momentCompat.locale = (locale?: any, config?: any) => {
    if (typeof locale === 'string') {
        if (config != null) {
            try {
                (dayjs as any).updateLocale(locale, config);
            } catch (e) {
                // dayjs locale keys differ from moment's — non-fatal.
                dayjs.locale(locale);
            }
        } else {
            dayjs.locale(locale);
        }
    }
    return dayjs.locale();
};

momentCompat.utc = (...args: any[]) => (dayjs as any).utc(...args);

momentCompat.unix = (...args: any[]) => (dayjs as any).unix(...args);

momentCompat.isMoment = (value: any) => dayjs.isDayjs(value);

/** moment.tz(...) — zone optional. Falls back to a plain parse when the dayjs `timezone`
 *  plugin is not loaded (see the note at the top of this file). */
momentCompat.tz = (...args: any[]) => {
    const zone = args[1];
    if (zone == null || typeof (dayjs as any).tz !== 'function') {
        return dayjs(args[0]);
    }
    if (args[2] != null) {
        return (dayjs as any).tz(args[0], args[2], zone);
    }
    return (dayjs as any).tz(args[0], zone);
};
// moment-timezone global default (the app previously forced UTC). Day.js has no equivalent global
// default; timezone conversions are still explicit via `.tz(zone)`. Kept as a no-op for API parity.
momentCompat.tz.setDefault = () => {};

/**
 * moment.duration(...) — supports ms number, {units} object, number + unit, and 'HH:mm[:ss]'
 * strings the way moment did.
 */
momentCompat.duration = (input?: any, unit?: any) => {
    if (typeof input === 'string') {
        const parsed = parseDurationString(input);
        if (parsed != null) {
            return dayjs.duration(parsed);
        }
    }
    return dayjs.duration(input, unit);
};

export default momentCompat;
export {dayjs};

/** Moment's type name — kept as `any` so existing typed call-sites compile unchanged. */
export type Moment = any;
